/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useEffect } from 'react';
import { placeholderImageUrl } from '../../components/image';
import { IAny, IGeneric } from '../../interfaces';
import { IEditableFieldProps } from './editable-field';
import { getEditableFieldClassName, isEditorialMode } from './utils';

export const ImageField = (props: IEditableFieldProps<IGeneric<IAny>>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [svgRenderer, setSvgRenderer] = React.useState<(svgProps: any) => JSX.Element>();
    const onKeyPress = (event: React.KeyboardEvent<HTMLImageElement>) => {
        // 'enter' key
        if (event.charCode === 13 && props.onChange) {
            event.preventDefault();
            props.onChange(event);
        }
    };

    const editableProps = props.disabled
        ? {}
        : {
              onKeyPress: onKeyPress,
              onDoubleClick: props.onChange,
              onMouseOver: props.onMouseOver,
              onFocus: props.onFocus,
              onBlur: props.onBlur,
              onMouseOut: props.onMouseOut,
              tabIndex: 0
          };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fieldProps: any = {
        ...props.fieldProps,
        editProps: undefined
    };

    // If the image source is the placeholder image, set it's height to 1px
    if (
        props.fieldProps.src === placeholderImageUrl &&
        (!props.fieldProps['data-src'] || props.fieldProps['data-src'] === placeholderImageUrl)
    ) {
        fieldProps.height = '1px';
    }

    useEffect(() => {
        // if we are in editorial mode(WYSIWYG), dynamically load svg chunk and render placeholder SVG for empty images
        if (fieldProps.placeholderConfig && props.requestContext && isEditorialMode(props.requestContext)) {
            void import('../svg-loader').then(svg => {
                let SvgComponent: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
                switch (fieldProps.placeholderConfig?.name) {
                    case svg.DEFAULT_PLACEHOLDER:
                        SvgComponent = svg.default.largePicturePlaceholder;
                        break;
                    // TODO:Add more case when we get full assets of placeholder SVGs
                    case svg.LARGE_IMAGE_PLACEHOLDER:
                        SvgComponent = svg.default.largePicturePlaceholder;
                        break;
                    case svg.SMALL_IMAGE_PLACEHOLDER:
                        SvgComponent = svg.default.smallPicturePlaceholder;
                        break;
                    case svg.TEST_PLACEHOLDER:
                        SvgComponent = svg.default.testPlaceholder;
                        break;
                    default:
                        SvgComponent = svg.default.largePicturePlaceholder;
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                setSvgRenderer(() => (svgProps: React.SVGAttributes<SVGElement>) => {
                    return <SvgComponent {...svgProps} />;
                });
            });
        }
    }, [fieldProps.placeholderConfig]);

    if (fieldProps.placeholderConfig && props.requestContext && isEditorialMode(props.requestContext)) {
        const divStyle = {
            display: 'flex',
            backgroundColor: '#F2F2F2',
            alignItems: 'center',
            width: (fieldProps.placeholderConfig.svgConfig && fieldProps.placeholderConfig.svgConfig.width) || '100%',
            height: (fieldProps.placeholderConfig.svgConfig && fieldProps.placeholderConfig.svgConfig.height) || '100%'
        };
        const svgStyle = {
            margin: '0 auto',
            display: 'block'
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const svgPropety: React.SVGAttributes<SVGElement> = {
            style: svgStyle
        };
        return (
            <div
                style={divStyle}
                {...editableProps}
                className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
            >
                {svgRenderer && svgRenderer(svgPropety)}
            </div>
        );
    }
    if (fieldProps.imageFallbackOptimize) {
        return (
            <div>
                <img
                    {...fieldProps}
                    {...editableProps}
                    className={`${getEditableFieldClassName(
                        props.disabled,
                        props.fieldProps.className,
                        props.requestContext
                    )} msc-img_fallback_optimize`}
                    {...(props.fieldProps.additionalProperties || {})}
                />
            </div>
        );
    } else {
        return (
            <img
                {...fieldProps}
                {...editableProps}
                className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
                {...(props.fieldProps.additionalProperties || {})}
            />
        );
    }
};
